import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContent, RUN_STATUS, RUN_STATUS_COLORS, RUN_STATUS_LABELS, RUN_STATUS_LABELS_COLORS, getLastProcessStepId } from '../../context/ContentContext';
import { ResultsPanel } from './ResultsPanel';
import { FiChevronDown } from 'react-icons/fi';
import { IoMdCheckmarkCircle, IoMdArrowBack } from 'react-icons/io';
import { MdCircle } from 'react-icons/md';
import { GiSandsOfTime } from "react-icons/gi";
import { FaCircle, FaRegCircle, FaRegCirclePause } from "react-icons/fa6";
import './RunProgress.css';

const NON_LOADING_STEPS = new Set(['download']);

const LoadingClock = () => (
  <div className="loading-clock-container">
    <FaCircle className="loading-clock-fill" />
    <FaRegCircle className="loading-clock-outline" />
  </div>
);

const StatusIcon = ({ status, stepId }) => {
  if (status === 'completed') return <IoMdCheckmarkCircle size={20} />;
  if (status === 'waiting' || status === 'stopped') return <FaRegCirclePause size={20} color="#216585" />;
  if (status === 'pending') return <GiSandsOfTime size={20} />;
  if (status === 'in_progress') {
    return NON_LOADING_STEPS.has(stepId) ? <IoMdCheckmarkCircle size={20} /> : <LoadingClock size={20} />;
  }
  return <MdCircle size={20} opacity={0.3} />;
};

const getStepClass = (status) => {
  const statusMap = {
    completed: 'run-progress-step--completed',
    in_progress: 'run-progress-step--active',
    waiting: 'run-progress-step--waiting',
    stopped: 'run-progress-step--waiting',
    pending: 'run-progress-step--pending',
  };
  return `run-progress-step ${statusMap[status] || statusMap.pending}`;
};

const ProgressPanel = ({ steps, statusColor, statusLabel, statusLabelColors, onStepClick, currentActiveStep }) => {
  const isClickable = (id, status) => {
    if (id === 'start') return false;
    return ['completed', 'waiting', 'stopped', 'in_progress'].includes(status);
  };

  const visibleSteps = steps.filter(({ id, status }) => {
    if (id === 'feedback') return false;
    if ((id === 'download') && status === 'pending') {
      return false;
    }
    return true;
  });

  return (
    <div className="run-progress-panel">
      <header className="run-progress-panel__header">
        <h3 className="run-progress-panel__title">Progress</h3>
        <div className="run-progress-panel__status">
          <span className="run-progress-panel__status-indicator" style={{ backgroundColor: statusColor }} />
          <span className="run-progress-panel__status-label" style={{ color: statusLabelColors }}>{statusLabel}</span>
        </div>
      </header>

      <p className="run-progress-panel__hint">
        Click on the steps below to see results generated separately.
      </p>

      <div className="run-progress-panel__steps">
        {visibleSteps.map(({ id, label, status }, index) => {
          const isActive = id === currentActiveStep;
          const canClick = isClickable(id, status);
          
          return (
            <div key={id} className="run-progress-step-wrapper">
              <div 
                className={`${getStepClass(status)} ${canClick ? 'run-progress-step--clickable' : 'run-progress-step--not-clickable'} ${isActive ? 'run-progress-step--clicked' : ''} ${id === 'start' ? 'run-progress-step--start' : ''}`} 
                onClick={() => canClick && onStepClick(id)}
                style={{ cursor: canClick ? 'pointer' : 'default' }}
              >
                <StatusIcon status={status} stepId={id} />
                <span className="run-progress-step__label">{label}</span>
              </div>
              {index < visibleSteps.length - 1 && <FiChevronDown size={16} className="run-progress-step__chevron" />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const RunProgressPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { runProgress, improveContent, goToStep, startRun, generateMaterials, stopRun } = useContent();
  const { isRunning, status, currentStep, config, results, error, steps } = runProgress;
  const lastProcessStepForFeedback = getLastProcessStepId(steps);
  const [prompt, setPrompt] = useState('');
  const [hasTriggeredContinue, setHasTriggeredContinue] = useState(false);

  useEffect(() => {
    const state = location.state;
    
    if (!config && !state?.config) {
      navigate('/', { replace: true });
      return;
    }

    if (state?.shouldGenerateMaterials && state?.runId && !hasTriggeredContinue) {
      setHasTriggeredContinue(true);
      
      const timer = setTimeout(() => {
        generateMaterials();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [location.state, config, navigate, hasTriggeredContinue, generateMaterials]);

  if (!config && !location.state?.config) return null;

  const statusColor = RUN_STATUS_COLORS[status] ?? RUN_STATUS_COLORS[RUN_STATUS.WAITING];
  const statusLabel = RUN_STATUS_LABELS[status] ?? RUN_STATUS_LABELS[RUN_STATUS.WAITING];
  const statusLabelColors = RUN_STATUS_LABELS_COLORS[status] ?? RUN_STATUS_LABELS_COLORS[RUN_STATUS.WAITING];


  const handleEditRun = () => {
    if (!config) return;
    navigate('/', { state: { reopenModal: true, editConfig: config } });
  };

  return (
    <div className="run-progress-page">
      <button className="button button--secondary buttons-pattern button-back-content" onClick={() => navigate('/')}>
        <span className="button__icon button__icon--left"><IoMdArrowBack /></span>
        <span className="button__text">Back to content list</span>
      </button>
      <main className="run-progress-page__main">
        <ProgressPanel
          steps={steps}
          statusColor={statusColor}
          statusLabel={statusLabel}
          statusLabelColors={statusLabelColors}
          onStepClick={goToStep}
          currentActiveStep={currentStep}
        />
        <ResultsPanel
          steps={steps}
          isRunning={isRunning}
          status={status}
          config={config}
          currentStep={currentStep}
          currentMessage={runProgress.currentMessage}
          results={results}
          error={error}
          improvementPrompt={prompt}
          onPromptChange={(e) => setPrompt(e.target.value)}
          onImprove={() => {improveContent(prompt.trim());  setPrompt('');}}
          onStopRun={stopRun}
          onContinueRun={() => startRun(config)}
          onReturnToFeedback={() => lastProcessStepForFeedback && goToStep(lastProcessStepForFeedback)}
          onGenerateMaterials={generateMaterials}
          onEditRun={handleEditRun}
        />
      </main>
    </div>
  );
};